import { create } from 'zustand'

import { LoginScope, LoginSource } from '@/consts/team'
import { AuthService, LoginStep1Result } from '@/services/auth-service'
import { AccountManager } from '@/stores/shared/account-manager'
import { useTeamStore } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import { getRegisterChannelFromCookies } from '@/utils/adAttribution'
import {
  clearAllAccounts,
  LOGIN_TOKEN_COOKIE_EXPIRY_DAYS,
  LOGIN_TOKEN_EXPIRY_MS,
  removeToken,
  setToken,
  TeamTokenData,
} from '@/utils/auth'

interface AuthStoreState {
  _saveAccountIfNeeded: (
    token: string,
    email: string,
    scope: number | undefined,
    saveAccount: boolean,
  ) => Promise<void>
  _finalizeLogin: (
    token: string,
    email: string,
    scope: number | undefined,
    saveAccount: boolean,
  ) => Promise<void>
  loginStep1: (email: string, password: string, scope?: number) => Promise<LoginStep1Result>
  completeMfaLogin: (
    mfaChallenge: string,
    totpCode: string,
    email: string,
    saveAccount?: boolean,
    scope?: number,
  ) => Promise<{ code: number; msg?: string } | void>
  completeMfaLoginWithRecovery: (
    mfaChallenge: string,
    recoveryCode: string,
    email: string,
    saveAccount?: boolean,
    scope?: number,
  ) => Promise<{ code: number; msg?: string } | void>
  login: (email: string, password: string, saveAccount?: boolean, scope?: number) => Promise<void>
  loginByOauth: (
    oauth_code: string,
    oauth_type: string,
    inviter_code?: string,
    saveAccount?: boolean,
  ) => Promise<void>
  registerByOauth: (
    oauth_code: string,
    oauth_type: string,
    password: string,
    saveAccount?: boolean,
  ) => Promise<void>
  logout: () => void
  logoutAllAccounts: () => void
}

export const useAuthStore = create<AuthStoreState>((_, get) => ({
  async _saveAccountIfNeeded(
    token: string,
    email: string,
    scope: number | undefined,
    saveAccount: boolean,
  ) {
    const userState = useUserStore.getState()
    await AccountManager.saveAccountAfterLogin(
      token,
      email,
      { id: userState.id, name: userState.name },
      scope,
      saveAccount,
    )
  },

  async _finalizeLogin(
    token: string,
    email: string,
    scope: number | undefined,
    saveAccount: boolean,
  ) {
    if (scope === LoginScope.Team) {
      const initialTokenData: TeamTokenData = {
        token,
        teamId: 0,
        teamResID: '',
        expiryTime: Date.now() + LOGIN_TOKEN_EXPIRY_MS,
      }
      useTeamStore.getState().setTeamContext(initialTokenData, LOGIN_TOKEN_COOKIE_EXPIRY_DAYS)
      useTeamStore
        .getState()
        .setPrimaryTeamContext(initialTokenData, LOGIN_TOKEN_COOKIE_EXPIRY_DAYS)

      await useUserStore.getState().getUserInfo()

      const teamState = useTeamStore.getState()
      const userState = useUserStore.getState()
      if (teamState.scopeInfo?.scope === 1 && teamState.scopeInfo.scopeID) {
        const completeTokenData: TeamTokenData = {
          token,
          teamId: teamState.scopeInfo.scopeID,
          teamResID: teamState.scopeInfo.resID,
          expiryTime: Date.now() + LOGIN_TOKEN_EXPIRY_MS,
          userName: userState.name,
          userRole: teamState.teamTokenData?.userRole,
        }
        useTeamStore.getState().setTeamContext(completeTokenData, LOGIN_TOKEN_COOKIE_EXPIRY_DAYS)
        useTeamStore
          .getState()
          .setPrimaryTeamContext(completeTokenData, LOGIN_TOKEN_COOKIE_EXPIRY_DAYS)
      }

      await Promise.all([
        useUserStore.getState().getAccount(),
        useUserStore.getState().getVolume(),
        useUserStore.getState().getSshPubKey(),
      ])

      useTeamStore.getState().updateLoginSource(LoginSource.Team)
    } else {
      setToken(token, true)
      await Promise.all([
        useUserStore.getState().getUserInfo(),
        useUserStore.getState().getAccount(),
        useUserStore.getState().getVolume(),
        useUserStore.getState().getSshPubKey(),
      ])

      useTeamStore.getState().updateLoginSource(LoginSource.Platform)
      await get()._saveAccountIfNeeded(token, email, scope, saveAccount)
    }
  },

  async loginStep1(email: string, password: string, scope?: number) {
    if (scope === LoginScope.Team) {
      return AuthService.teamLoginStep1(email, password)
    }
    return AuthService.platformLoginStep1(email, password)
  },

  async completeMfaLogin(
    mfaChallenge: string,
    totpCode: string,
    email: string,
    saveAccount: boolean = true,
    scope?: number,
  ) {
    const result = await AuthService.verifyMfaLogin(mfaChallenge, totpCode, { noErrMsg: true })

    if ('code' in result) {
      return result
    }

    await get()._finalizeLogin(result.token, email, scope, saveAccount)
  },

  async completeMfaLoginWithRecovery(
    mfaChallenge: string,
    recoveryCode: string,
    email: string,
    saveAccount: boolean = true,
    scope?: number,
  ) {
    const result = await AuthService.verifyMfaLoginWithRecovery(mfaChallenge, recoveryCode, {
      noErrMsg: true,
    })

    if ('code' in result) {
      return result
    }

    await get()._finalizeLogin(result.token, email, scope, saveAccount)
  },

  async login(email: string, password: string, saveAccount: boolean = true, scope?: number) {
    const step1 = await get().loginStep1(email, password, scope)

    if (step1.mfaRequired) {
      throw new Error('MFA required')
    }

    await get()._finalizeLogin(step1.token, email, scope, saveAccount)
  },

  async loginByOauth(
    oauth_code: string,
    oauth_type: string,
    inviter_code: string | undefined,
    saveAccount: boolean = true,
  ) {
    const result = await AuthService.oauthLogin(oauth_code, oauth_type, inviter_code)

    setToken(result.token, true)
    await Promise.all([
      useUserStore.getState().getUserInfo(),
      useUserStore.getState().getAccount(),
      useUserStore.getState().getVolume(),
      useUserStore.getState().getSshPubKey(),
    ])

    useTeamStore.getState().updateLoginSource(LoginSource.Platform)

    const userState = useUserStore.getState()
    const createTimeSeconds = userState.createTime
    const nowSeconds = Math.floor(Date.now() / 1000)
    const isNewUser = nowSeconds - createTimeSeconds < 180
    if (isNewUser) {
      const registerChannel = getRegisterChannelFromCookies()
      if (registerChannel) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'Sign_Up_Success',
          email: userState.email,
          register_channel: registerChannel,
        })

        await new Promise((resolve) => setTimeout(resolve, 500))
      }
    }

    await get()._saveAccountIfNeeded(result.token, userState.email, undefined, saveAccount)
  },

  async registerByOauth(
    oauth_code: string,
    oauth_type: string,
    password: string,
    saveAccount: boolean = true,
  ) {
    const result = await AuthService.oauthRegister(oauth_code, oauth_type, password)

    setToken(result.token, true)
    await Promise.all([
      useUserStore.getState().getUserInfo(),
      useUserStore.getState().getAccount(),
      useUserStore.getState().getVolume(),
      useUserStore.getState().getSshPubKey(),
    ])

    useTeamStore.getState().updateLoginSource(LoginSource.Platform)

    const userState = useUserStore.getState()
    await get()._saveAccountIfNeeded(result.token, userState.email, undefined, saveAccount)
  },

  logout() {
    removeToken()

    const accounts = AccountManager.getStoredAccounts()
    const activeAccountId = AccountManager.getActiveAccountId()

    if (accounts.length <= 1 && activeAccountId) {
      clearAllAccounts()
    }

    useTeamStore.getState().clearLoginSourceState()
    useTeamStore.getState().clearTeamContext()
    useTeamStore.getState().clearPrimaryTeamContext()

    useUserStore.setState({ id: 0 })
  },

  logoutAllAccounts() {
    try {
      AccountManager.clearAll()

      useTeamStore.getState().clearLoginSourceState()
      useTeamStore.getState().clearTeamContext()
      useTeamStore.getState().clearPrimaryTeamContext()

      useUserStore.setState({
        id: 0,
        name: '',
        email: '',
      })
    } catch (error) {
      console.error('Failed to logout all accounts:', error)
      clearAllAccounts()
      useTeamStore.getState().clearLoginSourceState()
      useTeamStore.getState().clearTeamContext()
      useTeamStore.getState().clearPrimaryTeamContext()
      useUserStore.setState({
        id: 0,
        name: '',
        email: '',
      })
    }
  },
}))
